import React, { useEffect, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  ClipboardList, Search, SlidersHorizontal, ChevronDown, ChevronRight,
  CheckCircle, XCircle, Clock, Users, Award, Code2, Copy
} from "lucide-react";
import Editor from "@monaco-editor/react";
import apiClient from "../../utils/apiClient";
import DataTable from "../Common/DataTable";
import { PageHeader, AppBadge } from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";
import { getCurrentUser } from "../../utils/auth";
import { formatToReadableDateTime } from "../../utils/common";
import "./Assignments.scss";

const SESSION_STATUSES = ["", "pending", "active", "submitted", "expired"];

const statusStyle = {
  pending: { variant: "secondary", label: "Pending" },
  active: { variant: "info", label: "In Progress" },
  submitted: { variant: "success", label: "Submitted" },
  expired: { variant: "danger", label: "Expired" },
};

const AssignmentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const prefix = getCurrentUser()?.usertype === "instructor" ? "/i" : "";

  const [assignment, setAssignment] = useState(null);
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [searchInput, setSearchInput] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/assignments/${id}/results`, {
        params: { ...filters, page, limit: pageSize },
      });
      setAssignment(data.assignment);
      setSummary(data.summary);
      setResults(data.results || []);
      setTotalRecords(data.total || 0);
    } catch {
      setAssignment(null);
      setResults([]);
    }
    setLoading(false);
  }, [id, filters, page, pageSize]);

  useEffect(() => { fetchResults(); }, [fetchResults]);
  useEffect(() => { setPage(1); }, [filters]);

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) setFilters(f => ({ ...f, search: searchInput }));
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleExpand = (studentId) => {
    setExpandedRow(prev => prev === studentId ? null : studentId);
  };

  const activeFilterCount = filters.status ? 1 : 0;

  const columns = [
    {
      key: "expand",
      label: "",
      width: "40px",
      render: (r) => (
        <button
          className="asd-expand-btn"
          onClick={(e) => { e.stopPropagation(); toggleExpand(r.student._id); }}
          disabled={r.sessionStatus === "pending"}
        >
          {expandedRow === r.student._id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ),
    },
    {
      key: "index",
      label: "#",
      width: "45px",
      render: (_, index) => (page - 1) * pageSize + index + 1,
    },
    {
      key: "student",
      label: "Student",
      width: "22%",
      render: (r) => (
        <div className="asd-student-cell">
          <img
            src={`https://api.dicebear.com/7.x/micah/svg?seed=${encodeURIComponent(r.student.name)}`}
            alt=""
            className="asd-avatar"
          />
          <div>
            <div className="fw-medium">{r.student.name}</div>
            <div className="asd-student-email">{r.student.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "10%",
      render: (r) => {
        const s = statusStyle[r.sessionStatus] || statusStyle.pending;
        return <AppBadge variant={s.variant}>{s.label}</AppBadge>;
      },
    },
    {
      key: "score",
      label: "Score",
      width: "12%",
      render: (r) => {
        if (r.sessionStatus !== "submitted") return <span className="text-muted">—</span>;
        const scoreVal = r.scoreOutOf100 ?? (r.totalTests > 0 ? Math.round((r.totalPassed / r.totalTests) * 100) : 0);
        const allPassed = scoreVal === 100;
        return (
          <div className="asd-score-cell">
            <span className={`asd-score-100 ${allPassed ? "asd-score-100--perfect" : scoreVal >= 50 ? "asd-score-100--good" : "asd-score-100--low"}`}>
              {scoreVal}<span className="asd-score-100-max">/100</span>
            </span>
            <span className="asd-score-tc-sub">{r.totalPassed}/{r.totalTests} test cases</span>
          </div>
        );
      },
    },
    {
      key: "testCases",
      label: "Questions",
      width: "10%",
      render: (r) => {
        if (r.sessionStatus !== "submitted" || !r.totalQuestions) return <span className="text-muted">—</span>;
        const allPassed = r.score === r.totalQuestions;
        return (
          <span className={`asd-score ${allPassed ? "asd-score--pass" : "asd-score--partial"}`}>
            {r.score}/{r.totalQuestions} passed
          </span>
        );
      },
    },
    {
      key: "startedAt",
      label: "Started",
      width: "14%",
      render: (r) => r.startedAt ? formatToReadableDateTime(r.startedAt) : "—",
    },
    {
      key: "submittedAt",
      label: "Submitted",
      width: "14%",
      render: (r) => r.submittedAt ? formatToReadableDateTime(r.submittedAt) : "—",
    },
  ];

  /* ── Expanded row renderer ── */
  const renderExpandedRow = (r) => {
    if (expandedRow !== r.student._id || !r.answers?.length) return null;

    return (
      <tr key={`exp-${r.student._id}`} className="asd-expanded-row">
        <td colSpan={columns.length}>
          <div className="asd-answers">
            {r.answers.map((ans, qi) => {
              const lang = Object.keys(ans.code || {})[0] || "—";
              const code = ans.code?.[lang] || "";
              const visible = (ans.results || []).filter(t => !t.isHidden);
              const hidden = (ans.results || []).filter(t => t.isHidden);
              const hiddenPassed = hidden.filter(t => t.passed).length;

              return (
                <div key={qi} className="asd-answer-card">
                  <div className="asd-answer-header">
                    <span className="asd-answer-q">Q{qi + 1}</span>
                    <AppBadge variant={ans.status === "passed" ? "success" : ans.status === "failed" ? "danger" : "secondary"}>
                      {ans.status}
                    </AppBadge>
                    <span className={`asd-answer-score-100 ${ans.total > 0 && ans.passedCount === ans.total ? "asd-answer-score-100--perfect" : ""}`}>
                      {ans.total > 0 ? Math.round((ans.passedCount / ans.total) * 100) : 0}/100
                    </span>
                    <span className="asd-answer-tc">
                      <CheckCircle size={12} /> {ans.passedCount}/{ans.total} test cases
                    </span>
                    {lang !== "—" && (
                      <span className="asd-answer-lang">
                        <Code2 size={12} /> {lang}
                      </span>
                    )}
                  </div>

                  {/* Test case results */}
                  {visible.length > 0 && (
                    <div className="asd-tc-grid">
                      {visible.map((tc, ti) => (
                        <div key={ti} className={`asd-tc-item ${tc.passed ? "asd-tc-item--pass" : "asd-tc-item--fail"}`}>
                          <div className="asd-tc-status">
                            {tc.passed ? <CheckCircle size={12} className="text-success" /> : <XCircle size={12} className="text-danger" />}
                            <span>Case {ti + 1}</span>
                          </div>
                          <div className="asd-tc-data">
                            <div><span className="asd-tc-label">Input</span><code>{tc.input || "(empty)"}</code></div>
                            <div><span className="asd-tc-label">Expected</span><code>{tc.expected}</code></div>
                            <div><span className="asd-tc-label">Output</span><code>{tc.output || "—"}</code></div>
                          </div>
                        </div>
                      ))}
                      {hidden.length > 0 && (
                        <div className={`asd-tc-item ${hiddenPassed === hidden.length ? "asd-tc-item--pass" : "asd-tc-item--fail"} asd-tc-item--hidden`}>
                          <div className="asd-tc-status">
                            {hiddenPassed === hidden.length ? <CheckCircle size={12} className="text-success" /> : <XCircle size={12} className="text-danger" />}
                            <span>{hiddenPassed}/{hidden.length} hidden test case{hidden.length !== 1 ? "s" : ""} passed</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Submitted code */}
                  {code && (
                    <div className="asd-code-block">
                      <div className="asd-code-header">
                        <span>Submitted Code ({lang})</span>
                        <button className="asd-copy-btn" onClick={() => handleCopyCode(code)}>
                          <Copy size={12} />
                          {copiedCode === code ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <Editor
                        height={Math.min(300, Math.max(100, code.split("\n").length * 19))}
                        language={lang}
                        value={code}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 13,
                          lineNumbers: "on",
                          folding: false,
                          contextmenu: false,
                          renderLineHighlight: "none",
                          padding: { top: 8 },
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </td>
      </tr>
    );
  };

  if (loading && !assignment) {
    return <div className="d-flex justify-content-center my-5"><Spinner animation="border" /></div>;
  }

  if (!assignment) {
    return <div className="text-muted text-center my-5">No assignment data found.</div>;
  }

  return (
    <div className="asd-page">
      <PageHeader
        backButton
        onBack={() => navigate(`${prefix}/assignments`)}
        icon={ClipboardList}
        title={assignment.title}
        description={`Batch: ${assignment.batch} · ${assignment.numQuestions} questions · ${assignment.timeLimit || "—"} min`}
      />

      {/* Summary Stats */}
      <div className="asd-stats">
        <div className="asd-stat-card">
          <Users size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">{summary?.totalStudents || 0}</div>
            <div className="asd-stat-label">Total Students</div>
          </div>
        </div>
        <div className="asd-stat-card asd-stat-card--success">
          <CheckCircle size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">{summary?.completed || 0}</div>
            <div className="asd-stat-label">Completed</div>
          </div>
        </div>
        <div className="asd-stat-card asd-stat-card--warning">
          <Clock size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">{summary?.pending || 0}</div>
            <div className="asd-stat-label">Pending</div>
          </div>
        </div>
        <div className="asd-stat-card asd-stat-card--info">
          <Award size={18} className="asd-stat-icon" />
          <div>
            <div className="asd-stat-value">
              {summary?.completed && summary?.totalStudents
                ? Math.round((summary.completed / summary.totalStudents) * 100)
                : 0}%
            </div>
            <div className="asd-stat-label">Completion Rate</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="asl-toolbar">
        <div className="asl-search">
          <Search size={16} className="asl-search-icon" />
          <input
            type="text"
            className="asl-search-input"
            placeholder="Search students..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        <div className="asl-toolbar-actions">
          <select
            className="asl-filter-select d-none d-md-block"
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            {SESSION_STATUSES.filter(Boolean).map(s => (
              <option key={s} value={s}>{(statusStyle[s]?.label) || s}</option>
            ))}
          </select>

          <button
            className={`asl-filter-btn d-md-none${activeFilterCount > 0 ? " asl-filter-btn--active" : ""}`}
            onClick={() => setShowFilterModal(true)}
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && <span className="asl-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      <FilterModal
        show={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onReset={() => setFilters(f => ({ ...f, status: "" }))}
        activeCount={activeFilterCount}
      >
        <FilterGroup label="Status">
          <select
            className="asl-filter-select"
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            {SESSION_STATUSES.filter(Boolean).map(s => (
              <option key={s} value={s}>{(statusStyle[s]?.label) || s}</option>
            ))}
          </select>
        </FilterGroup>
      </FilterModal>

      <DataTable
        data={results}
        columns={columns}
        loading={loading}
        hoverable
        pagination={{
          page,
          pageSize,
          total: totalRecords,
          onPageChange: setPage,
        }}
        emptyMessage="No student results found"
        renderExpandedRow={renderExpandedRow}
      />
    </div>
  );
};

export default AssignmentDetails;
